'use client';

import { JSX, useMemo, useRef } from 'react';
import orMiniCtaBlockC from './or-mini-cta-block-c.variants';
import classNames from 'classnames';

// components
import { MlModal } from '@/components/molecules/ml-modal/ml-modal';
import { OrLeadGenForm } from '../or-lead-gen-form';
import { AtDivider } from '@/components/atoms/at-divider/at-divider';
import { AtMedia } from '@/components/atoms/at-media/at-media';
import { AtHeader } from '@/components/atoms/at-header/at-header';
import { AtClickable } from '@/components/atoms/at-clickable/at-clickable';
import { AtIcon } from '@/components/atoms/at-icon/at-icon';

// types
import {
  EClickableSize,
  EClickableVariants,
} from '@/components/atoms/at-clickable/at-clickable.types';
import { IOrMiniCtaBlockCProps } from './or-mini-cta-block-c.types';
import {
  IModalRefProps,
  EModalVariants,
} from '@/components/molecules/ml-modal/ml-modal.types';
import {
  EDividerColors,
  EDividerDirectionalVariants,
  EDividerVariants,
} from '@/components/atoms/at-divider/at-divider.types';

// utils + hooks
import { sendGTMClickEvent } from '@/components/helpers/analytics/gtm';

/**
 * `OrMiniCtaBlockC` is a vertically stacked CTA block with support for an image,
 * a title, optional description, and a CTA. If `leadGenForm` is provided, the CTA
 * opens a modal with the form.
 *
 * This variant is ideal for content that benefits from a visual element and needs a full-width layout.
 *
 * CTA buttons can include icons, and interaction location is dynamically inferred for GTM tracking.
 *
 */
export const OrMiniCtaBlockC = ({
  children,
  theme,
  image,
  content_title,
  title_element = 'h2',
  title_variant = 'h2',
  cta,
  topSpacing = 'md',
  bottomSpacing = 'md',
  topDivider = false,
  bottomDivider = false,
  leadGenForm,
}: IOrMiniCtaBlockCProps): JSX.Element => {
  const modalRef = useRef<IModalRefProps | null>(null);

  const handleOpen = () => {
    modalRef?.current?.handleOpen();

    sendGTMClickEvent(
      cta?.label || 'Schedule Now',
      'or-mini-cta-block-c',
      'form-pop-up',
    );
  };

  const hasDescription = !!children;

  const {
    base,
    contents,
    title: titleClasses,
    descriptionContainer,
  } = orMiniCtaBlockC({
    theme,
    hasDescription,
    topSpacing,
    bottomSpacing,
  });

  const interactionLocationNoModal = useMemo(() => {
    let bannerInteractionLocationNoModal = 'or-mini-cta-block-c';

    if (
      cta?.href?.includes('youtube') &&
      cta?.label?.toLowerCase().includes('tour')
    ) {
      bannerInteractionLocationNoModal = 'video-tour';
    }

    if (
      cta?.href?.includes('matterport') &&
      cta?.label?.toLowerCase().includes('tour')
    ) {
      bannerInteractionLocationNoModal = 'virtual-tour';
    }

    return bannerInteractionLocationNoModal;
  }, [cta?.href, cta?.label]);

  // todo: update theme to dynamic val
  return (
    <div data-theme="light">
      <aside data-testid="or-mini-cta-block-c" className="container">
        {topDivider && (
          <AtDivider
            decorative
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
            customClasses="container"
          />
        )}
        <div className={classNames(base())}>
          <div data-theme={theme} className={contents()}>
            {image && (
              <div className="h-[10.625rem] w-auto lg:h-[13.5rem]">
                <AtMedia
                  {...image}
                  className="!relative"
                  sizes="(max-width: 1023px) 50vw, 33.3vw"
                />
              </div>
            )}

            <div className="mt-8 flex flex-col gap-y-6 lg:mt-0 lg:gap-x-6">
              {content_title && (
                <AtHeader
                  variant={title_variant}
                  element={title_element}
                  className={titleClasses()}
                >
                  {content_title}
                </AtHeader>
              )}
              {children && (
                <span className={descriptionContainer()}>{children}</span>
              )}
            </div>
            {leadGenForm && (
              <AtClickable
                label={cta?.label || 'Schedule Now'}
                variant={cta?.variant || EClickableVariants.PRIMARY}
                size={cta?.size || EClickableSize.LG}
                outline={cta?.outline}
                onClick={handleOpen}
                type={cta?.type}
                interactLocation="or-mini-cta-block-c"
                className="mt-6 h-fit w-fit text-nowrap lg:mt-0"
                iconPosition="left"
                shouldSendGTMEvent={false}
              >
                {cta?.icon && (
                  <AtIcon
                    icon={cta?.icon}
                    color="var(--color-button-fill-primary)"
                  />
                )}
              </AtClickable>
            )}
            {!leadGenForm && cta && (
              <AtClickable
                {...cta}
                iconPosition="left"
                interactLocation={interactionLocationNoModal}
                className="mt-6 h-fit w-fit text-nowrap lg:mt-0"
              >
                {cta?.icon && (
                  <AtIcon
                    icon={cta?.icon}
                    color="var(--color-button-fill-primary)"
                  />
                )}
              </AtClickable>
            )}
            {leadGenForm && (
              <MlModal
                className="theme-light"
                variant={EModalVariants.LEAD_GEN}
                ref={modalRef}
              >
                <OrLeadGenForm
                  formId={leadGenForm.form_id}
                  formName={leadGenForm.title}
                  heading={leadGenForm.heading}
                  description={leadGenForm.description}
                  headingElement={leadGenForm.heading_element?.select}
                  headingVariant={leadGenForm.heading_variant?.select}
                  formType={leadGenForm.form_type}
                  isInModal={leadGenForm.is_in_modal}
                  topSpacing={leadGenForm.top_spacing?.spacing_value}
                  bottomSpacing={leadGenForm.bottom_spacing?.spacing_value}
                />
              </MlModal>
            )}
          </div>
        </div>
        {bottomDivider && (
          <AtDivider
            decorative
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
            customClasses="container"
          />
        )}
      </aside>
    </div>
  );
};
